import React, { useContext, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { OrderScreenContext } from 'features/order-summary/screens/CheckoutScreen';

import { OrderSummaryOptions, showHeight } from './summary/OrderSummaryOptions';

import { useRootSelector } from 'rootStore';
import { createStyle } from 'utils/createStyle';
import { monetizeWithDecimal } from 'common/utils/strings';
import { Btn } from 'components/btn/Btn';
import { EASE_IN_OUT_BACK } from 'styles/easing';
import { getPath, Routes } from 'routes';
import { useFocusMonitor } from 'hooks/useFocusMonitor';
import { FeatureFlag } from 'components/feature-flag';

const styles = {
    orderDetailsContainerMobile: createStyle('absolute bg-white top-0 right-0 left-0 block', {
        boxShadow: '0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 4px 6px -1px rgba(0, 0, 0, 0.1)',
    }),
    orderDetails: 'text-brand-dark flex p-4 bg-grey-7 w-full items-center h-16',
    area: [
        'mb-2',
        {
            overflow: 'hidden',
            animationName: showHeight,
            animationDuration: '.5s',
            animationTimingFunction: EASE_IN_OUT_BACK,
            animationFillMode: 'forwards',
        },
    ],
};

export interface OrderSummaryDetailsMobileProps {
    shipToHome: boolean;
}

export const OrderSummaryDetailsMobile = ({ shipToHome }: OrderSummaryDetailsMobileProps) => {
    const [expandItems, setExpandItems] = useState(false);
    const { cartInfo, cartInfoDetails } = useContext(OrderScreenContext);
    const { total } = cartInfoDetails;
    const cart = useRootSelector((s) => s.store.cart.cart);
    const isCartEmpty = !cart || cart.lineItems.length <= 0;

    const history = useHistory();

    const beginCheckout = () => {
        const url = getPath(Routes.ShopOrderPayment, {
            shipToHome: shipToHome ? 'ship-to-home' : 'ship-to-coordinator',
        });
        history.push(url, cartInfo);
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const isFocused = useFocusMonitor(containerRef);

    useEffect(() => {
        if (!isFocused) {
            setExpandItems(false);
        }
    }, [isFocused]);

    return (
        <div
            className={classNames(styles.orderDetailsContainerMobile, expandItems ? 'z-10' : '')}
            ref={containerRef}
        >
            <button type="button" className="w-full" onClick={() => setExpandItems((_) => !_)}>
                <div className={styles.orderDetails}>
                    <div className="text-xl font-bold">Order Details</div>
                    <div className="ml-auto mr-4">{monetizeWithDecimal(total)}</div>
                    <div>
                        {!expandItems && <ChevronDown />}
                        {expandItems && <ChevronUp />}
                    </div>
                </div>
            </button>
            <div className="bg-white p-4" style={{ display: expandItems ? '' : 'none' }}>
                <div>
                    {!isCartEmpty && (
                        <OrderSummaryOptions shipToHome={shipToHome}>
                            <div className={classNames(styles.area, 'hidden md:block mt-5')}>
                                <FeatureFlag
                                    flagKey="enable_checkout_button"
                                    displayOnValue={true}
                                    fallback={true}
                                >
                                    <Btn className="w-full mt-4" onClick={beginCheckout}>
                                        Begin Checkout
                                    </Btn>
                                </FeatureFlag>
                                <FeatureFlag
                                    flagKey="enable_checkout_button"
                                    displayOnValue={false}
                                >
                                    <Btn className="w-full mt-4" disabled>
                                        Checkout Disabled
                                    </Btn>
                                </FeatureFlag>
                            </div>
                        </OrderSummaryOptions>
                    )}
                    {isCartEmpty && <div className="text-grey-4">Empty cart.</div>}
                </div>
            </div>
        </div>
    );
};
